export const CHARACTER_INDEX_EXTERNAL_MODEL_PAYLOAD_KIND = 'variorum-character-index-model-payload'

export interface CharacterIndexExternalModelPayloadCall {
  call_id: string
  prompt_text: string
  prompt_version?: string
  output_contract: string
  source_text_length: number
  source_row_ids?: readonly string[]
  window?: Record<string, unknown>
  prepared_extraction_call: unknown
}

export interface CharacterIndexExternalModelPayload {
  export_kind: typeof CHARACTER_INDEX_EXTERNAL_MODEL_PAYLOAD_KIND
  schema_version: 1
  created_at: string
  detector_id: string
  detector_label: string
  run_mode: string
  output_contract: string
  model: {
    model_id?: string
    temperature?: number
    thinking_level?: string
  }
  provenance: Record<string, unknown>
  focused_character?: {
    character_id: string
    display_name: string
    aliases?: readonly string[]
  }
  calls: CharacterIndexExternalModelPayloadCall[]
  import_shape: {
    single_call: string
    windowed: string
  }
}

export interface CharacterIndexExternalResultWindow {
  windowId?: string
  windowIndex?: number
  rawExtraction: unknown
}

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
)

export const stripJsonMarkdownFence = (value: string): string => {
  const trimmed = value.trim()
  const match = trimmed.match(/^```(?:json|JSON)?\s*([\s\S]*?)\s*```$/)
  return match ? match[1].trim() : trimmed
}

export const parseCharacterIndexExternalClipboardJson = (value: string): unknown => (
  JSON.parse(stripJsonMarkdownFence(value))
)

const parseNestedJsonString = (value: unknown): unknown | null => {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = stripJsonMarkdownFence(value)
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null
  }
  try {
    return JSON.parse(trimmed)
  } catch {
    return null
  }
}

const looksLikePackageRawExtraction = (value: unknown): boolean => {
  const record = asRecord(value)
  if (!record) {
    return Array.isArray(value)
  }
  return Array.isArray(record.characters) ||
    Array.isArray(record.figures) ||
    Array.isArray(record.focalization) ||
    Array.isArray(record.characterizations) ||
    Array.isArray(record.occurrences) ||
    Array.isArray(record.entries) ||
    Array.isArray(record.mentions) ||
    Array.isArray(record.results)
}

export const extractCharacterIndexExternalRawExtraction = (value: unknown): unknown | null => {
  const parsedString = parseNestedJsonString(value)
  if (parsedString) {
    return extractCharacterIndexExternalRawExtraction(parsedString)
  }
  if (looksLikePackageRawExtraction(value)) {
    return value
  }
  const record = asRecord(value)
  if (!record) {
    return null
  }

  const directCandidates = [
    record.raw_extraction,
    record.rawExtraction,
    record.result,
    record.results,
    record.output,
    record.response,
    record.data,
    record.json,
  ]
  for (const candidate of directCandidates) {
    if (candidate === value) {
      continue
    }
    const extracted = extractCharacterIndexExternalRawExtraction(candidate)
    if (extracted) {
      return extracted
    }
  }

  const choices = Array.isArray(record.choices) ? record.choices : []
  for (const choice of choices) {
    const choiceRecord = asRecord(choice)
    const message = asRecord(choiceRecord?.message)
    const content = message?.content || choiceRecord?.text
    const extracted = extractCharacterIndexExternalRawExtraction(content)
    if (extracted) {
      return extracted
    }
  }

  const candidates = Array.isArray(record.candidates) ? record.candidates : []
  for (const candidate of candidates) {
    const candidateRecord = asRecord(candidate)
    const content = asRecord(candidateRecord?.content)
    const parts = content && Array.isArray(content.parts) ? content.parts : []
    for (const part of parts) {
      const extracted = extractCharacterIndexExternalRawExtraction(asRecord(part)?.text)
      if (extracted) {
        return extracted
      }
    }
  }

  return null
}

export const extractCharacterIndexExternalResultWindows = (value: unknown): CharacterIndexExternalResultWindow[] => {
  const record = asRecord(value)
  const windows: unknown[] = record && Array.isArray(record.windows)
    ? record.windows
    : record && Array.isArray(record.window_results)
      ? record.window_results
      : record && Array.isArray(record.windowResults)
        ? record.windowResults
        : []

  return windows.flatMap((item, index): CharacterIndexExternalResultWindow[] => {
    const windowRecord = asRecord(item)
    if (!windowRecord) {
      const rawExtraction = extractCharacterIndexExternalRawExtraction(item)
      return rawExtraction ? [{ windowIndex: index, rawExtraction }] : []
    }
    const windowMeta = asRecord(windowRecord.window)
    const rawExtraction = extractCharacterIndexExternalRawExtraction(windowRecord.raw_extraction || windowRecord.rawExtraction || windowRecord.result || windowRecord.output || item)
    if (!rawExtraction) {
      return []
    }
    return [{
      windowId: typeof windowRecord.window_id === 'string'
        ? windowRecord.window_id
        : typeof windowRecord.windowId === 'string'
          ? windowRecord.windowId
          : typeof windowMeta?.windowId === 'string'
            ? windowMeta.windowId
            : undefined,
      windowIndex: typeof windowRecord.window_index === 'number'
        ? windowRecord.window_index
        : typeof windowRecord.windowIndex === 'number'
          ? windowRecord.windowIndex
          : typeof windowMeta?.windowIndex === 'number'
            ? windowMeta.windowIndex
            : index,
      rawExtraction,
    }]
  })
}
